import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EarlyPayoutConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { DeviceService } from '@frontend/vanilla/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { CashoutSettingsBetInfo, CashoutSettingsType, IAutoCashoutBetslipNumbers } from '../../../../my-bets-base/models/early-payout';
import { NumpadSource, StakeChangedEvent } from '../../../../numpad/model';
import { NumpadService } from '../../../../numpad/numpad.service';
import { CashoutSettingsService } from '../../../services/cash-out-settings.service';

@Component({
    selector: 'ms-my-bets-earlypayout-autocashout',
    templateUrl: 'my-bets-earlypayout-autocashout.component.html',
    providers: [NumpadService],
    standalone: false,
})
export class MyBetsEarlyPayoutAutocashoutComponent implements OnInit {
    autocashoutValue = 0;
    validationError: Nullable<string> = null;
    @Input() betNumbers: IAutoCashoutBetslipNumbers;
    @Input() type: CashoutSettingsType;
    @Input() betTypeInfo: string;

    bodyText = '';
    minMaxText = '';
    private betInfo: Nullable<CashoutSettingsBetInfo> = null;
    hasMinMaxFailture = false;
    isMobile = false;
    readonly numpadSource = NumpadSource.AutoCashout;

    private hasStakeError = new BehaviorSubject<boolean>(false);
    hasStakeError$: Observable<boolean> = this.hasStakeError.asObservable();
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private deviceService: DeviceService,
        public sitecore: Sitecore,
        private csService: CashoutSettingsService,
        private epConfig: EarlyPayoutConfig,
    ) {}

    get autocashoutValueString(): string {
        return this.autocashoutValue.toString();
    }

    ngOnInit(): void {
        this.isMobile = this.deviceService.isMobile;
        this.csService.settingsChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((betid: string) => this.autocashoutValueChanged(betid));
        this.csService.errorsCleared.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((betid: string) => this.clearErrors(betid));
        if (CashoutSettingsType.Auto === this.type) {
            this.bodyText = this.sitecore.earlyPayoutV2.SetAutocashoutBodyText;
            this.minMaxText = this.sitecore.earlyPayoutV2.SetAutocashoutMinMaxBodyText;
        } else {
            this.bodyText = this.sitecore.earlyPayoutV2.SetAutocashoutNotificationBodyText;
            this.minMaxText = this.sitecore.earlyPayoutV2.SetAutocashoutNotificationMinMaxBodyText;
        }

        this.betInfo = this.csService.getBetInfo(this.betNumbers.betslipId);
        this.checkMinMax();
    }

    private clearErrors(betId: string): void {
        if (betId === this.betInfo!.betslipId) {
            this.validationError = null;
            this.hasStakeError.next(false);

            this.autocashoutValue = 0;
        }
    }

    private checkMinMax(): void {
        this.hasMinMaxFailture =
            this.betInfo!.grossPossibleWinnings.value - 0.01 < this.betInfo!.earlyPayout!.value + this.epConfig.autoCashOutMarginValue;
    }

    autocashoutValueChanged(betId: string): void {
        if (this.betNumbers.betslipId && betId !== this.betNumbers.betslipId) {
            return;
        }

        const bet = this.csService.getBetInfo(this.betNumbers.betslipId);
        let numericValue;

        if (this.type === CashoutSettingsType.Notify) {
            numericValue = bet!.earlyPayout!.autoCashoutNotificationValue;
        } else if (this.type === CashoutSettingsType.Auto) {
            numericValue = bet!.earlyPayout!.autoCashOutValue;
        }

        if (numericValue) {
            this.autocashoutValue = numericValue;
        } else {
            this.autocashoutValue = 0;
            this.checkMinMax();
        }
    }

    onStakeChanged(e: StakeChangedEvent): void {
        this.validationError = null;
        this.hasStakeError.next(false);

        this.autocashoutValue = +e.stake || 0;
    }

    onError(err: string | null): void {
        this.validationError = err;
        this.hasStakeError.next(!!err);
    }
}
